// ts-rest contract for the unified review-queue surface.
//
// Phase 2 of the /api/db/:table removal (OLD_CODE.html #1). The web hook
// `use-review-queue.ts` previously made 13 separate `useTable` calls
// against the generic CRUD surface to assemble the "items pending
// review" list. This endpoint replaces those reads with a single
// persona-gated server-side aggregation.
//
// The server-side service:
//   - reads every artefact summary table filtered to `reviewStatus =
//     'pending'`,
//   - denormalises `clientName` from the Client ontology so the hook
//     does not need a separate ontology_clients fetch,
//   - returns rows keyed by table name so the hook's existing merge
//     logic (collapse post-call artefacts per call, group composer
//     drafts, etc.) can run unchanged.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const reviewStatusSchema = z
    .enum(['pending', 'approved', 'rejected', 'revision_requested'])
    .nullable()
    .optional();

/**
 * Common shape every artefact row honours after the runtime writes it via
 * `create_artefact` — `id`, `reviewStatus`, plus a small set of common
 * content fields used to build the queue-item summary line. The hook
 * reads only these. Per-artefact-type detail panels resolve the rest via
 * their own typed endpoint or `useRow`.
 */
export const artefactSummaryRowSchema = z.object({
    id: z.string(),
    reviewStatus: reviewStatusSchema,
    /** Denormalised on the server for the queue summary line. */
    clientId: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    // Content fields the summary line falls back to in priority order.
    summary: z.string().nullable().optional(),
    subject: z.string().nullable().optional(),
    headline: z.string().nullable().optional(),
    claim: z.string().nullable().optional(),
    ruleStatement: z.string().nullable().optional(),
    signalSummary: z.string().nullable().optional(),
    // Composer drafts carry tonal variants — pick variants[0] when present.
    variants: z
        .array(z.object({ body: z.string().nullable().optional() }).nullable().optional())
        .nullable()
        .optional(),
});
export type ArtefactSummaryRow = z.infer<typeof artefactSummaryRowSchema>;

/** Post-call artefact rows — keyed by `callId` for per-call grouping. */
export const callArtefactRowSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    reviewStatus: reviewStatusSchema,
});
export type CallArtefactRow = z.infer<typeof callArtefactRowSchema>;

/** Monthly narrative drafts — keyed by `(clientId, period)`. */
export const monthlyNarrativeRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable().optional(),
    period: z.string(),
    reviewStatus: reviewStatusSchema,
});
export type MonthlyNarrativeRow = z.infer<typeof monthlyNarrativeRowSchema>;

/**
 * Internal Suitability Assessment awaiting a second reviewer. Unlike the agent
 * artefacts above, this is a staff-authored document (no Reviewable cap, so no
 * `reviewStatus` column) — its "needs review" state is structural: the IM has
 * `completed` (or is still drafting) the assessment but no peer reviewer has
 * signed it off yet (`peerReviewName` unset). The Desk Suitability page lists
 * these as a lightweight second-review queue (Pages 7-8 feedback); each row
 * deep-links to the assessment form so the reviewer opens it populated.
 */
export const suitabilityAssessmentReviewRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable().optional(),
    status: z.enum(['draft', 'completed']),
    completedAt: z.string().nullable().optional(),
    investmentManagerName: z.string().nullable().optional(),
});
export type SuitabilityAssessmentReviewRow = z.infer<typeof suitabilityAssessmentReviewRowSchema>;

export const reviewQueuePendingResponseSchema = z.object({
    // Post-call artefacts grouped per call.
    callExtractedFacts: z.array(callArtefactRowSchema),
    callActionItems: z.array(callArtefactRowSchema),
    callKbNotes: z.array(callArtefactRowSchema),
    // ATLAS monthly narrative.
    wealthMonthlyNarrativeDrafts: z.array(monthlyNarrativeRowSchema),
    // Internal Suitability Assessments awaiting a second reviewer (staff-
    // authored, not an agent artefact — surfaced on the Desk Suitability page).
    suitabilityAssessmentReviews: z.array(suitabilityAssessmentReviewRowSchema),
    // VIGIL + detector findings.
    wealthVigilFindings: z.array(artefactSummaryRowSchema),
    onboardingStallFindings: z.array(artefactSummaryRowSchema),
    vulnerabilityMarkerFindings: z.array(artefactSummaryRowSchema),
    pcfDeviationFindings: z.array(artefactSummaryRowSchema),
    investmentTrustPolicyFindings: z.array(artefactSummaryRowSchema),
    dataFreshnessFindings: z.array(artefactSummaryRowSchema),
    // Composer drafts (SCRIVENER / HERALD / EMISSARY / CHARTER / HAVEN /
    // FORUM / ARBITER).
    suitabilityLetterDrafts: z.array(artefactSummaryRowSchema),
    onboardingPackDrafts: z.array(artefactSummaryRowSchema),
    clientCommunicationDrafts: z.array(artefactSummaryRowSchema),
    fundReportDrafts: z.array(artefactSummaryRowSchema),
    vulnerabilityAdaptationPlanDrafts: z.array(artefactSummaryRowSchema),
    governancePaperDrafts: z.array(artefactSummaryRowSchema),
    fairValueAssessmentDrafts: z.array(artefactSummaryRowSchema),
});
export type ReviewQueuePendingResponse = z.infer<typeof reviewQueuePendingResponseSchema>;

/**
 * Per-frontend pending-review tallies for the always-mounted shell chrome
 * (sidebar "Review queue" badge + agent-panel peek). `itemCount` is the
 * number of review *items* (post-call drafts collapse to one item per
 * call, composer drafts / findings / tasks are one each); `decisionCount`
 * is the number of individual approve/reject decisions those items carry.
 *
 * This is the lightweight counterpart to `pending`: it returns only
 * numbers, never artefact rows or grouped review items, so the chrome can
 * show an accurate badge without assembling the full review-queue model
 * (PRD #156). The full `pending` payload remains the source of truth for
 * the review-queue page and the agent review surfaces.
 */
export const frontendReviewCountsSchema = z.object({
    itemCount: z.number().int().nonnegative(),
    decisionCount: z.number().int().nonnegative(),
});
export type FrontendReviewCounts = z.infer<typeof frontendReviewCountsSchema>;

export const reviewQueueCountsResponseSchema = z.object({
    desk: frontendReviewCountsSchema,
    ops: frontendReviewCountsSchema,
    comply: frontendReviewCountsSchema,
    view: frontendReviewCountsSchema,
});
export type ReviewQueueCountsResponse = z.infer<typeof reviewQueueCountsResponseSchema>;

export const reviewQueueContract = c.router(
    {
        pending: {
            method: 'GET',
            path: '/review-queue/pending',
            responses: {
                200: reviewQueuePendingResponseSchema,
            },
            summary: 'Aggregated per-table summary of every artefact row pending human review',
        },
        counts: {
            method: 'GET',
            path: '/review-queue/counts',
            responses: {
                200: reviewQueueCountsResponseSchema,
            },
            summary: 'Lightweight per-frontend pending-review counts for shell chrome badges',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
